import { motion } from "framer-motion";
import { Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { renderLinkedText, resolveImage } from "@/lib/content";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export const WhoWeServe = () => {
  const { content } = useContent();
  const s = content.homepage_serve;
  return (
    <section className="bg-[#FAF8F5] py-24 lg:py-32" data-testid="who-we-serve-section">
      <div className="mx-auto max-w-4xl px-6 text-left lg:px-10">
        <motion.div {...reveal}>
          <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#7A2B21]">
            {s.kicker}
          </p>
          <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#1C1917] sm:text-5xl">
            {s.title1} <span className="italic text-[#7A2B21]">{s.title2}</span>
          </h2>
          <p className="mt-8 text-base leading-relaxed text-[#57534E] md:text-lg">
            {renderLinkedText(s.body)}
          </p>
          <p className="mt-8 flex items-start gap-3 border-l-2 border-[#C5A059] pl-6 font-display text-lg italic leading-relaxed text-[#1C1917]">
            <Globe size={20} className="mt-1 shrink-0 text-[#C5A059]" />
            {renderLinkedText(s.countries_note)}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export const HomeAbout = () => {
  const { content } = useContent();
  const a = content.homepage_about;
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="home-about-section">
      <div className="grid items-center gap-14 lg:grid-cols-12">
        <motion.div {...reveal} className="lg:col-span-6">
          <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#7A2B21]">
            {a.kicker}
          </p>
          <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#1C1917] sm:text-5xl">
            {a.title1} <span className="italic text-[#7A2B21]">{a.title2}</span>
          </h2>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-[#57534E]">
            {renderLinkedText(a.body)}
          </p>
          <Link
            to={a.cta_href || "/about"}
            data-testid="home-about-cta"
            className="mt-10 inline-block rounded-full border border-[#7A2B21] px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#7A2B21] transition-[background-color,color,transform] duration-300 hover:bg-[#7A2B21] hover:text-[#FAF8F5] hover:scale-[1.03]"
          >
            {a.cta_label}
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6"
        >
          <div className="arch-frame overflow-hidden shadow-[0_30px_60px_rgba(28,25,23,0.15)]">
            <img
              src={resolveImage(content.how.image)}
              alt={content.how.image_alt || "The Chandigarh Tricity region our local team serves"}
              className="hero-image-grade h-[400px] w-full object-cover"
              loading="lazy"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
